/**
 * LoginPage.jsx
 * 로그인/회원가입 페이지 - 기존 login.html 1:1 이관
 */

import { useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import api, { apiClient } from '../services/api';

import TermsModal from '../components/modals/TermsModal';

export default function LoginPage({ initialTab = 'login' }) {
    const navigate = useNavigate();
    const [activeTab, setActiveTab] = useState(initialTab); // 'login' | 'register'
    const [message, setMessage] = useState({ text: '', type: '' });
    const [isLoading, setIsLoading] = useState(false);
    const [isTermsOpen, setIsTermsOpen] = useState(false);
    const [termsAgreed, setTermsAgreed] = useState(false);

    // OTP 인증 단계
    const [otpStep, setOtpStep] = useState(false);
    const [otpCode, setOtpCode] = useState('');
    const [pendingUsername, setPendingUsername] = useState('');

    // 로그인 폼 상태
    const [loginForm, setLoginForm] = useState({ username: '', password: '' });

    // 회원가입 폼 상태
    const [registerForm, setRegisterForm] = useState({
        username: '',
        nickname: '',
        email: '',
        password: '',
        passwordConfirm: ''
    });

    const showMessage = useCallback((text, type = 'error') => {
        setMessage({ text, type });
    }, []);

    const hideMessage = useCallback(() => {
        setMessage({ text: '', type: '' });
    }, []);

    // 로그인 처리
    const handleLogin = async (e) => {
        e.preventDefault();
        if (!loginForm.username || !loginForm.password) {
            showMessage('ID와 비밀번호를 입력해주세요.', 'error');
            return;
        }

        setIsLoading(true);
        hideMessage();

        try {
            const data = await api.login(loginForm.username, loginForm.password);

            if (data.error) {
                showMessage(data.error, 'error');
                setIsLoading(false);
                return;
            }

            if (!data.user.is_approved) {
                showMessage('관리자 승인 대기 중입니다. 승인 후 이용 가능합니다.', 'warning');
                setIsLoading(false);
                return;
            }

            // localStorage에 username 저장
            localStorage.setItem('coding_tutor_user_id', loginForm.username);

            // [Fix] 게스트 데모 데이터가 남아있을 수 있으므로 persist 캐시 클리어 후 Store 리셋
            localStorage.removeItem('luna-storage');

            // [Fix] 로그인 즉시 스토어 상태 업데이트 (데모 데이터 제거 후 사용자 세션만 설정)
            const useLunaStore = (await import('../store/useLunaStore')).default;
            useLunaStore.getState().resetAll(); // 게스트 데모 데이터 제거
            useLunaStore.getState().setUserSession(data.user); // 사용자 세션 설정

            showMessage('로그인 성공! 메인 페이지로 이동합니다.', 'success');
            setTimeout(() => {
                // [Fix] 리로드 대신 replace로 완전히 새로운 상태로 앱 진입
                window.location.href = '/app';
            }, 800);

        } catch (err) {
            showMessage('서버와 연결할 수 없습니다. 네트워크를 확인해주세요.', 'error');
            setIsLoading(false);
            console.error('[LOGIN ERROR]', err);
        }
    };

    // 회원가입 처리
    const handleRegister = async (e) => {
        e.preventDefault();

        // 약관 동의 체크
        if (!termsAgreed) {
            showMessage('서비스 이용약관에 동의해주세요.', 'error');
            return;
        }

        // 닉네임 검증
        if (!registerForm.nickname || registerForm.nickname.trim().length < 2) {
            showMessage('닉네임을 2자 이상 입력해주세요.', 'error');
            return;
        }

        // 이메일 검증
        const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
        if (!registerForm.email || !emailRegex.test(registerForm.email.trim())) {
            showMessage('올바른 이메일 주소를 입력해주세요.', 'error');
            return;
        }

        // 비밀번호 확인
        if (registerForm.password !== registerForm.passwordConfirm) {
            showMessage('비밀번호가 일치하지 않습니다.', 'error');
            return;
        }

        if (registerForm.password.length < 8) {
            showMessage('비밀번호는 8자 이상이어야 합니다.', 'error');
            return;
        }

        setIsLoading(true);
        hideMessage();

        try {
            const data = await api.register({
                username: registerForm.username,
                password: registerForm.password,
                nickname: registerForm.nickname,
                email: registerForm.email.trim().toLowerCase(),
            });

            if (data.error) {
                showMessage(data.error, 'error');
                setIsLoading(false);
                return;
            }

            setIsLoading(false);

            if (data.status === 'otp_sent') {
                setPendingUsername(registerForm.username);
                setOtpStep(true);
                showMessage(data.message, 'success');
            } else if (data.status === 'pending') {
                showMessage(data.message, 'warning');
                setRegisterForm({ username: '', nickname: '', email: '', password: '', passwordConfirm: '' });
                setTermsAgreed(false);
                setTimeout(() => setActiveTab('login'), 3000);
            } else {
                showMessage(data.message, 'success');
                setRegisterForm({ username: '', nickname: '', email: '', password: '', passwordConfirm: '' });
                setTermsAgreed(false);
                setTimeout(() => setActiveTab('login'), 2000);
            }

        } catch (err) {
            const msg = err?.message || '서버와 연결할 수 없습니다. 네트워크를 확인해주세요.';
            showMessage(msg, 'error');
            setIsLoading(false);
            console.error('[REGISTER ERROR]', err);
        }
    };

    // poscoenc.com OTP 인증 처리
    const handleVerifyOtp = async (e) => {
        e.preventDefault();
        if (otpCode.length !== 6) {
            showMessage('6자리 인증 코드를 입력해 주세요.', 'error');
            return;
        }
        setIsLoading(true);
        hideMessage();
        try {
            const res = await apiClient.post('/verify-email', { username: pendingUsername, code: otpCode });
            const data = res.data;
            if (data.error) {
                showMessage(data.error, 'error');
                setIsLoading(false);
                return;
            }
            showMessage(data.message, 'success');
            setOtpStep(false);
            setOtpCode('');
            setIsLoading(false);
            setTimeout(() => setActiveTab('login'), 2000);
        } catch (err) {
            const msg = err?.response?.data?.error || err?.response?.data?.detail || '서버와 연결할 수 없습니다.';
            showMessage(msg, 'error');
            setIsLoading(false);
        }
    };

    // 약관 동의 처리
    const handleTermsAgree = useCallback(() => {
        setTermsAgreed(true);
        setIsTermsOpen(false);
    }, []);

    return (
        <div className="min-h-screen bg-[#0B0E14] text-slate-200 flex flex-col font-sans">
            {/* 배경 효과 */}
            <div className="fixed inset-0 overflow-hidden pointer-events-none">
                <div className="absolute top-1/4 left-1/4 w-96 h-96 bg-blue-600 rounded-full blur-[150px] opacity-10"></div>
                <div className="absolute bottom-1/4 right-1/4 w-96 h-96 bg-purple-600 rounded-full blur-[150px] opacity-10"></div>
            </div>

            {/* 헤더 */}
            <header className="flex items-center justify-center px-5 py-4 bg-[#0f1219]/80 border-b border-slate-800 shrink-0 z-20">
                <div
                    className="flex items-center gap-3 select-none cursor-pointer group"
                    onClick={() => navigate('/')}
                >
                    {/* Main Logo */}
                    <h1 className="text-4xl font-black tracking-tight leading-none font-sans">
                        <span className="text-white group-hover:text-slate-100 transition-colors">LU</span>
                        <span className="text-purple-500 group-hover:text-purple-400 transition-colors">NA</span>
                    </h1>
                    {/* Full Name - Matched Height with Logo */}
                    <div className="flex flex-col justify-center leading-tight">
                        <span className="text-[11px] font-bold text-slate-400 tracking-wide">Logic Unified</span>
                        <span className="text-[11px] font-bold text-purple-400/80 tracking-wide">Neural Adviser</span>
                    </div>
                </div>
            </header>

            {/* 메인 컨텐츠 */}
            <main className="flex-grow flex items-center justify-center p-6 relative z-10">
                <div className="bg-[rgba(15,18,25,0.9)] backdrop-blur-xl border border-slate-700/20 rounded-2xl p-8 w-full max-w-md shadow-2xl">

                    {/* 탭 헤더 */}
                    <div className="flex mb-8 border-b border-slate-700">
                        <button
                            onClick={() => { setActiveTab('login'); hideMessage(); }}
                            className={`flex-1 py-3 text-center font-medium transition-all border-b-2 ${activeTab === 'login'
                                ? 'border-purple-500 text-purple-400'
                                : 'border-transparent text-slate-400 hover:text-slate-300'
                                }`}
                        >
                            <i className="fas fa-sign-in-alt mr-2"></i>로그인
                        </button>
                        <button
                            onClick={() => { setActiveTab('register'); hideMessage(); }}
                            className={`flex-1 py-3 text-center font-medium transition-all border-b-2 ${activeTab === 'register'
                                ? 'border-purple-500 text-purple-400'
                                : 'border-transparent text-slate-400 hover:text-slate-300'
                                }`}
                        >
                            <i className="fas fa-user-plus mr-2"></i>회원가입
                        </button>
                    </div>

                    {/* 알림 메시지 */}
                    <AnimatePresence>
                        {message.text && (
                            <motion.div
                                initial={{ opacity: 0, y: -10 }}
                                animate={{ opacity: 1, y: 0 }}
                                exit={{ opacity: 0, y: -10 }}
                                className={`mb-4 p-3 rounded-lg text-sm ${message.type === 'error' ? 'bg-red-500/10 border border-red-500/30 text-red-300' :
                                    message.type === 'success' ? 'bg-green-500/10 border border-green-500/30 text-green-300' :
                                        'bg-yellow-500/10 border border-yellow-500/30 text-yellow-300'
                                    }`}
                            >
                                {message.text}
                            </motion.div>
                        )}
                    </AnimatePresence>

                    {/* 로그인 폼 */}
                    {activeTab === 'login' && (
                        <form onSubmit={handleLogin} className="space-y-4">
                            <div>
                                <label className="block text-sm font-medium text-slate-400 mb-2">
                                    <i className="fas fa-user mr-1"></i>ID
                                </label>
                                <input
                                    type="text"
                                    value={loginForm.username}
                                    onChange={(e) => setLoginForm({ ...loginForm, username: e.target.value })}
                                    className="w-full px-4 py-3 rounded-lg bg-slate-800/60 border border-slate-700/30 text-white placeholder-slate-500 focus:outline-none focus:border-purple-500 focus:ring-2 focus:ring-purple-500/20 transition-all"
                                    placeholder="ID를 입력하세요"
                                    required
                                />
                            </div>
                            <div>
                                <label className="block text-sm font-medium text-slate-400 mb-2">
                                    <i className="fas fa-lock mr-1"></i>비밀번호
                                </label>
                                <input
                                    type="password"
                                    value={loginForm.password}
                                    onChange={(e) => setLoginForm({ ...loginForm, password: e.target.value })}
                                    className="w-full px-4 py-3 rounded-lg bg-slate-800/60 border border-slate-700/30 text-white placeholder-slate-500 focus:outline-none focus:border-purple-500 focus:ring-2 focus:ring-purple-500/20 transition-all"
                                    placeholder="비밀번호를 입력하세요"
                                    required
                                />
                            </div>
                            <button
                                type="submit"
                                disabled={isLoading}
                                className="w-full py-3 rounded-lg font-medium text-white shadow-lg shadow-purple-500/20 bg-gradient-to-r from-purple-600 to-blue-600 hover:from-purple-500 hover:to-blue-500 transition-all disabled:opacity-70 disabled:cursor-not-allowed"
                            >
                                {isLoading ? (
                                    <><i className="fas fa-spinner fa-spin mr-2"></i>처리 중...</>
                                ) : (
                                    <><i className="fas fa-arrow-right mr-2"></i>로그인</>
                                )}
                            </button>
                        </form>
                    )}

                    {/* OTP 인증 단계 */}
                    {activeTab === 'register' && otpStep && (
                        <form onSubmit={handleVerifyOtp} className="space-y-4">
                            <div className="text-center py-2">
                                <div className="text-4xl mb-3">📧</div>
                                <h3 className="text-lg font-bold text-white mb-1">이메일 인증</h3>
                                <p className="text-sm text-slate-400">
                                    <span className="text-purple-400 font-semibold">{pendingUsername}</span> 님의 이메일로<br />
                                    발송된 6자리 코드를 입력해 주세요.
                                </p>
                            </div>
                            <div>
                                <label className="block text-sm font-medium text-slate-400 mb-2">
                                    <i className="fas fa-key mr-1"></i>인증 코드
                                </label>
                                <input
                                    type="text"
                                    inputMode="numeric"
                                    maxLength={6}
                                    value={otpCode}
                                    onChange={(e) => setOtpCode(e.target.value.replace(/\D/g, ''))}
                                    autoFocus
                                    className="w-full px-4 py-3 rounded-lg bg-slate-800/60 border border-slate-700/30 text-white text-center text-2xl font-mono font-bold tracking-[0.4em] placeholder-slate-600 focus:outline-none focus:border-purple-500 focus:ring-2 focus:ring-purple-500/20 transition-all"
                                    placeholder="000000"
                                    required
                                />
                            </div>
                            <button
                                type="submit"
                                disabled={isLoading || otpCode.length !== 6}
                                className="w-full py-3 rounded-lg font-medium text-white shadow-lg shadow-purple-500/20 bg-gradient-to-r from-purple-600 to-blue-600 hover:from-purple-500 hover:to-blue-500 transition-all disabled:opacity-70 disabled:cursor-not-allowed"
                            >
                                {isLoading ? (
                                    <><i className="fas fa-spinner fa-spin mr-2"></i>인증 중...</>
                                ) : (
                                    <><i className="fas fa-check-circle mr-2"></i>인증 완료</>
                                )}
                            </button>
                            <button
                                type="button"
                                onClick={() => { setOtpStep(false); setOtpCode(''); hideMessage(); }}
                                className="w-full text-xs text-slate-500 hover:text-slate-300 transition-all py-1"
                            >
                                ← 가입 화면으로 돌아가기
                            </button>
                        </form>
                    )}

                    {/* 회원가입 폼 */}
                    {activeTab === 'register' && !otpStep && (
                        <form onSubmit={handleRegister} className="space-y-4">
                            <div>
                                <label className="block text-sm font-medium text-slate-400 mb-2">
                                    <i className="fas fa-user mr-1"></i>ID
                                </label>
                                <input
                                    type="text"
                                    value={registerForm.username}
                                    onChange={(e) => setRegisterForm({ ...registerForm, username: e.target.value })}
                                    className="w-full px-4 py-3 rounded-lg bg-slate-800/60 border border-slate-700/30 text-white placeholder-slate-500 focus:outline-none focus:border-purple-500 focus:ring-2 focus:ring-purple-500/20 transition-all"
                                    placeholder="사용할 ID를 입력하세요"
                                    required
                                />
                            </div>
                            <div>
                                <label className="block text-sm font-medium text-slate-400 mb-2">
                                    <i className="fas fa-id-badge mr-1"></i>닉네임
                                </label>
                                <input
                                    type="text"
                                    value={registerForm.nickname}
                                    onChange={(e) => setRegisterForm({ ...registerForm, nickname: e.target.value })}
                                    className="w-full px-4 py-3 rounded-lg bg-slate-800/60 border border-slate-700/30 text-white placeholder-slate-500 focus:outline-none focus:border-purple-500 focus:ring-2 focus:ring-purple-500/20 transition-all"
                                    placeholder="닉네임을 입력하세요 (2자 이상)"
                                    maxLength={20}
                                    required
                                />
                            </div>
                            <div>
                                <label className="block text-sm font-medium text-slate-400 mb-2">
                                    <i className="fas fa-envelope mr-1"></i>이메일
                                </label>
                                <input
                                    type="email"
                                    value={registerForm.email}
                                    onChange={(e) => setRegisterForm({ ...registerForm, email: e.target.value })}
                                    className="w-full px-4 py-3 rounded-lg bg-slate-800/60 border border-slate-700/30 text-white placeholder-slate-500 focus:outline-none focus:border-purple-500 focus:ring-2 focus:ring-purple-500/20 transition-all"
                                    placeholder="이메일 주소를 입력하세요"
                                    required
                                />
                                <p className="mt-1.5 text-xs text-slate-600 flex items-start gap-1.5">
                                    <i className="fas fa-shield-alt text-slate-500 mt-0.5 shrink-0"></i>
                                    가입 승인 및 알림 발송 목적으로만 사용되며, 암호화하여 안전하게 보관됩니다.
                                </p>
                            </div>
                            <div>
                                <label className="block text-sm font-medium text-slate-400 mb-2">
                                    <i className="fas fa-lock mr-1"></i>비밀번호
                                </label>
                                <input
                                    type="password"
                                    value={registerForm.password}
                                    onChange={(e) => setRegisterForm({ ...registerForm, password: e.target.value })}
                                    className="w-full px-4 py-3 rounded-lg bg-slate-800/60 border border-slate-700/30 text-white placeholder-slate-500 focus:outline-none focus:border-purple-500 focus:ring-2 focus:ring-purple-500/20 transition-all"
                                    placeholder="비밀번호 (8자 이상)"
                                    minLength={8}
                                    required
                                />
                            </div>
                            <div>
                                <label className="block text-sm font-medium text-slate-400 mb-2">
                                    <i className="fas fa-lock mr-1"></i>비밀번호 확인
                                </label>
                                <input
                                    type="password"
                                    value={registerForm.passwordConfirm}
                                    onChange={(e) => setRegisterForm({ ...registerForm, passwordConfirm: e.target.value })}
                                    className="w-full px-4 py-3 rounded-lg bg-slate-800/60 border border-slate-700/30 text-white placeholder-slate-500 focus:outline-none focus:border-purple-500 focus:ring-2 focus:ring-purple-500/20 transition-all"
                                    placeholder="비밀번호를 다시 입력하세요"
                                    minLength={8}
                                    required
                                />
                            </div>

                            {/* 이용약관 동의 */}
                            <div className="flex items-center gap-2 pt-2">
                                <input
                                    type="checkbox"
                                    id="agree-terms"
                                    checked={termsAgreed}
                                    disabled={!termsAgreed}
                                    onChange={() => { }}
                                    className={`w-4 h-4 rounded border-slate-600 bg-slate-700 text-purple-500 focus:ring-purple-500 focus:ring-offset-slate-900 ${!termsAgreed ? 'cursor-not-allowed opacity-60' : 'cursor-pointer'}`}
                                />
                                <label htmlFor="agree-terms" className="text-xs text-slate-400 select-none">
                                    <span className={termsAgreed ? 'text-emerald-400' : ''}>
                                        {termsAgreed ? '서비스 이용약관에 동의하였습니다.' : '서비스 이용약관에 동의가 필요합니다.'}
                                    </span>
                                    <button
                                        type="button"
                                        onClick={() => setIsTermsOpen(true)}
                                        className="text-purple-400 hover:text-purple-300 underline underline-offset-2 ml-1 font-medium"
                                    >
                                        {termsAgreed ? '다시 보기' : '약관 읽기'}
                                    </button>
                                </label>
                            </div>

                            <button
                                type="submit"
                                disabled={isLoading}
                                className="w-full py-3 rounded-lg font-medium text-white shadow-lg shadow-purple-500/20 bg-gradient-to-r from-purple-600 to-blue-600 hover:from-purple-500 hover:to-blue-500 transition-all disabled:opacity-70 disabled:cursor-not-allowed"
                            >
                                {isLoading ? (
                                    <><i className="fas fa-spinner fa-spin mr-2"></i>처리 중...</>
                                ) : (
                                    <><i className="fas fa-user-plus mr-2"></i>회원가입</>
                                )}
                            </button>
                            <p className="text-xs text-slate-500 text-center mt-2">
                                <i className="fas fa-info-circle mr-1"></i>회원가입 후 관리자 승인이 필요합니다.
                            </p>
                        </form>
                    )}
                </div>
            </main>

            {/* 푸터 */}
            <footer className="text-center py-4 text-xs text-slate-500 bg-[#0f1219]/50 border-t border-slate-800 relative z-10">
                © 2025 <strong className="text-slate-400">LUNA</strong> — Logic Unified Neural Advisor. All Rights Reserved.
            </footer>

            {/* 이용약관 모달 */}
            <AnimatePresence>
                {isTermsOpen && (
                    <TermsModal
                        isOpen={isTermsOpen}
                        onClose={() => setIsTermsOpen(false)}
                        onAgree={handleTermsAgree}
                    />
                )}
            </AnimatePresence>
        </div>
    );
}
